import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import CompanyIcon from '@/components/Icons/CompanyIcon';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import AddressSelectionDialogContent from '@/components/AddressSelectionDialog/AddressSelectionDialogContent';
import { Address } from '@/types/gearApi/Address';
import AddressDictionary from '@/types/dictionaries/Address';
import AddressSelectionDictionary from '@/types/dictionaries/AddressSelection';
import SearchIcon from '@mui/icons-material/Search';

export interface AddressSelectionDialogProps {
	addresses: Address[];
	dict: { addressCard: AddressDictionary; addressSelection: AddressSelectionDictionary };
	onClose: () => void;
	onContinue: (address: Address) => void;
	isOpen: boolean;
	preselectedAddress?: Address;
	isBillingProfileSelection?: boolean;
	forceUserToSelectAnAddress: boolean;
}

export default function AddressSelectionDialog({
	addresses,
	dict,
	onClose,
	onContinue,
	isOpen,
	preselectedAddress,
	isBillingProfileSelection = false,
	forceUserToSelectAnAddress,
}: Readonly<AddressSelectionDialogProps>) {
	const [selectedAddress, setSelectedAddress] = useState<Address | undefined>(preselectedAddress);
	const [searchTerm, setSearchTerm] = useState('');

	const handleContinue = () => {
		if (selectedAddress) {
			onContinue(selectedAddress);
		}
	};

	return (
		<Dialog
			open={isOpen}
			onClose={forceUserToSelectAnAddress ? undefined : onClose}
			aria-labelledby="address-selection-dialog-title"
			aria-describedby="address-selection-dialog-description"
			sx={{
				'& .MuiDialog-paper': {
					p: '32px',
					maxWidth: '900px',
				},
			}}
		>
			{!forceUserToSelectAnAddress && (
				<IconButton
					onClick={onClose}
					sx={{ position: 'absolute', width: '40px', height: '40px', top: '4px', right: '4px' }}
				>
					<CloseIcon />
				</IconButton>
			)}
			<DialogTitle
				id="address-selection-dialog-title"
				sx={{ display: 'flex', alignItems: 'center', gap: '8px', p: '0px', mb: '20px' }}
			>
				<Box sx={{ p: '8px', display: 'inline-flex', borderRadius: '300px', backgroundColor: 'primary.main' }}>
					<CompanyIcon fontSize="large" sx={{ width: '36px', height: '36px', color: 'white.main' }} />
				</Box>
				<Typography component="span" variant="h2" color="black.main">
					{dict.addressSelection.dialogTitle}
				</Typography>
			</DialogTitle>
			<Typography
				id="address-selection-dialog-description"
				variant="productInfo"
				sx={{ color: 'black.brand', mb: '20px' }}
			>
				{dict.addressSelection.dialogDescription}
			</Typography>
			<Typography variant="title" mb="12px">
				{dict.addressSelection.searchTitle}
			</Typography>
			<TextField
				label={dict.addressSelection.searchPlaceholder}
				variant="outlined"
				sx={{
					'& .MuiInputLabel-root[data-shrink="false"]': {
						marginLeft: '30px',
					},
					'& .MuiOutlinedInput-root': {
						paddingLeft: '30px',
					},
					mb: '24px',
				}}
				InputProps={{
					endAdornment: <SearchIcon sx={{ fontSize: '24px', color: 'black.main', position: 'absolute', left: '10px' }} />,
				}}
				inputProps={{
					'data-testid': 'address-selection-dialog-search-field',
				}}
				onChange={(e) => setSearchTerm(e.target.value)}
			/>
			<AddressSelectionDialogContent
				addresses={addresses}
				dict={dict.addressCard}
				searchTerm={searchTerm}
				selectedAddress={selectedAddress}
				onSelect={setSelectedAddress}
				isBillingProfileSelection={isBillingProfileSelection}
			/>
			<DialogActions sx={{ p: '0px' }}>
				{!forceUserToSelectAnAddress && (
					<Button variant="outlined" size="small" onClick={onClose}>
						{dict.addressSelection.cancelButton}
					</Button>
				)}
				<Button variant="contained" size="small" disabled={!selectedAddress} onClick={handleContinue}>
					{dict.addressSelection.continueButton}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
